import { useCallback, useEffect, useMemo } from 'react';

import { useTemplateQuery } from '../data/useTemplateQuery';
import { useCustomizationDispatch } from '../state/useCustomizationDispatch';
import { useCustomizationState } from '../state/useCustomizationState';
import {
  createTemplateCustomizationViewModel,
  type TemplateCustomizationViewModel,
} from '../viewmodels/createTemplateCustomizationViewModel';
import {
  resolvePageStatus,
  type PageStatus,
} from '../viewmodels/resolvePageStatus';

export type TemplateCustomization = {
  status: PageStatus;
  viewModel: TemplateCustomizationViewModel | null;
  handleRetry: () => void;
};

export function useTemplateCustomization(): TemplateCustomization {
  const { templateId, channelId, replica, replicaStatus, expandedBySectionId } =
    useCustomizationState();
  const dispatch = useCustomizationDispatch();
  const { template, failure, refresh } = useTemplateQuery({
    channelId,
    templateId,
  });

  useEffect(() => {
    if (template) {
      dispatch({ type: 'REPLICA_LOADED', template });
    }
  }, [template, dispatch]);

  useEffect(() => {
    if (failure) {
      dispatch({ type: 'REPLICA_LOAD_FAILED', failure });
    }
  }, [failure, dispatch]);

  const handleRetry = useCallback(() => {
    dispatch({ type: 'REPLICA_LOAD_STARTED' });
    refresh();
  }, [dispatch, refresh]);

  const status = resolvePageStatus({
    hasChannel: Boolean(channelId),
    hasTemplateId: Boolean(templateId),
    replicaStatus,
  });

  const viewModel = useMemo(
    () =>
      replica && status === 'ready'
        ? createTemplateCustomizationViewModel({
            template: replica,
            expandedBySectionId,
          })
        : null,
    [replica, status, expandedBySectionId]
  );

  return { status, viewModel, handleRetry };
}
